// src/pages/Login.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { engine } from '../core/books/storage.js';
import {
  syncUserWithRoomeet,
  withTimeout,
  saveRoomeetCreds,
} from '../services/roomeetAuth';
import './Login.css';
import {
  GRADE_LIST,
  FIELD_LIST,
  needsField,
  normalizeGradeId,
  normalizeFieldId,
  getGrade,
} from '../core/education/curriculum.js';

const Login = () => {
  const navigate = useNavigate();

  const { login: authLogin, updateUser: authUpdateUser } = useAuth();

  // 🌗 تم شب/روز — قفل روی تیره
const LOCK_DARK_THEME = true; // ← برای بازگشت به حالت عادی: false کن
const [isDarkMode, setIsDarkMode] = useState(LOCK_DARK_THEME ? true : (() => {
  try {
    return engine.getRaw('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
})());

const toggleTheme = useCallback(() => setIsDarkMode((prev) => !prev), []);



// 🔄 سینک بین تب‌های مرورگر
// ذخیره در کلید مشترک Navbar2
{/*useEffect(() => {
  try {
    // 🔧 FIX: استفاده از engine.setRaw → remoteSync آن را به userPrefs.theme سرور push می‌کند
    engine.setRaw('navbar2-drawer-dark', isDarkMode ? '1' : '0');
  } catch {}
}, [isDarkMode]);*/}

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  if (LOCK_DARK_THEME) return; // 🔒 تم قفل است

  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDarkMode(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      // 🔧 FIX: استفاده از engine.getRaw
      const current = engine.getRaw('navbar2-drawer-dark') === '1';
      setIsDarkMode((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  // ===== حالت‌های صفحه =====
  const STEPS = {
    PHONE: 'phone',
    LOGIN_PASSWORD: 'login-password',
    LOGIN_OTP: 'login-otp',
    FORGOT_PASSWORD: 'forgot-password',
    FORGOT_VERIFY: 'forgot-verify',
    FORGOT_RESET: 'forgot-reset',
    SIGNUP_VERIFY: 'signup-verify',
    SIGNUP_INFO: 'signup-info',
    SIGNUP_PASSWORD: 'signup-password',
  };

  // 🔙 مقصد بازگشت برای هر مرحله — اگه نباشه یعنی دکمه نمایش داده نمی‌شه
  const BACK_TARGETS = {
  [STEPS.LOGIN_PASSWORD]: STEPS.PHONE,
  [STEPS.LOGIN_OTP]: STEPS.LOGIN_PASSWORD,
  [STEPS.FORGOT_PASSWORD]: STEPS.LOGIN_PASSWORD,
  [STEPS.FORGOT_VERIFY]: STEPS.FORGOT_PASSWORD,
  [STEPS.FORGOT_RESET]: STEPS.LOGIN_PASSWORD,
  [STEPS.SIGNUP_VERIFY]: STEPS.PHONE,
  [STEPS.SIGNUP_INFO]: STEPS.PHONE,
  [STEPS.SIGNUP_PASSWORD]: STEPS.SIGNUP_INFO,
  };

  // 🖼 نقشه‌ی تصویر آواتار برای هر مرحله
const CHAR_IMAGES = {
  [STEPS.PHONE]:           '/images/avatar.png',
  [STEPS.LOGIN_PASSWORD]:  '/images/avatar2.png',
  [STEPS.LOGIN_OTP]:       '/images/avatar4.png',
  [STEPS.FORGOT_PASSWORD]: '/images/avatar3.png',
  [STEPS.FORGOT_VERIFY]:   '/images/avatar4.png',
  [STEPS.FORGOT_RESET]:    '/images/avatar5.png',
  [STEPS.SIGNUP_VERIFY]:   '/images/avatar4.png',
  [STEPS.SIGNUP_INFO]:     '/images/avatar7.png',
  [STEPS.SIGNUP_PASSWORD]: '/images/avatar6.png',
};

  // ===== State اصلی =====
  const [currentStep, setCurrentStep] = useState(STEPS.PHONE);
  const [phone, setPhone] = useState('');
  const [user, setUser] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sentOtp, setSentOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // ✅ وضعیت همگام‌سازی با Roomeet
  const [syncStatus, setSyncStatus] = useState('');

  // 🔙 بازگشت به مرحله‌ی قبلی
const handleBack = useCallback(() => {
  const target = BACK_TARGETS[currentStep];
  if (!target) return;
  setError('');
  setSuccessMessage('');
  setOtpCode('');
  setSentOtp('');
  setCanResend(false);
  setOtpTimer(0);
  setCurrentStep(target);
}, [currentStep]);

  // ===== State ثبت‌نام =====
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    role: 'student',
    grade: '',
    field: '',
    studentPhone: '',
    studentInfo: null,
    studentField: '',
    password: '',
    passwordConfirm: '',
  });
  const [signupErrors, setSignupErrors] = useState({});

  // ===== State فراموشی =====
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

  // ===== State نمایش رمز در ثبت‌نام =====
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupPasswordConfirm, setShowSignupPasswordConfirm] = useState(false);

  // ===== State قدرت رمز =====
  const [passwordStrength, setPasswordStrength] = useState(0);

  // ===== Refs =====
  const otpInputRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  // ============================================================
  //  mount/unmount guard
  // ============================================================
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ============================================================
  //  توابع کمکی
  // ============================================================
  const getPasswordStrength = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 10) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;
    if (score <= 2) return 0;
    if (score <= 4) return 1;
    return 2;
  };

  // ============================================================
  //  جلوه‌های جانبی
  // ============================================================
  useEffect(() => {
    const focusFirstInput = () => {
      setTimeout(() => {
        if (currentStep === STEPS.PHONE) {
          document.querySelector('input[name="phone"]')?.focus();
        } else if (currentStep === STEPS.LOGIN_PASSWORD) {
          document.querySelector('input[name="password"]')?.focus();
        } else if (
          [STEPS.LOGIN_OTP, STEPS.FORGOT_VERIFY, STEPS.SIGNUP_VERIFY].includes(
            currentStep
          )
        ) {
          otpInputRef.current?.focus();
        }
      }, 300);
    };
    focusFirstInput();
  }, [currentStep]);

  useEffect(() => {
    if (otpTimer > 0) {
      timerRef.current = setTimeout(() => setOtpTimer((prev) => prev - 1), 1000);
    } else if (otpTimer === 0 && sentOtp) {
      setCanResend(true);
    }
    return () => clearTimeout(timerRef.current);
  }, [otpTimer, sentOtp]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // ============================================================
  //  ✅ ورود موفق + همگام‌سازی خودکار با Roomeet
  //
  //  فلو:
  //   ۱. creds ذخیره میشه تو sessionStorage (قبل از هر کاری)
  //   ۲. authLogin → context آپدیت میشه
  //   ۳. navigate → کاربر فوراً وارد میشه
  //   ۴. sync در پس‌زمینه (fire-and-forget) با timeout 30s
  // ============================================================
  const handleLoginSuccess = async (userData, plainPassword = null) => {
    const { password: _pwd, ...safeUser } = userData;

    // ✅ normalize نام نمایشی
    const displayName =
      safeUser.fullName ||
      safeUser.name ||
      [safeUser.firstName, safeUser.lastName].filter(Boolean).join(' ').trim();
    if (displayName) {
      safeUser.name = displayName;
      safeUser.fullName = displayName;
    }

    const passwordToUse = plainPassword || userData?.password || null;

    // ✅ ۱. creds رو قبل از هر کاری ذخیره کن
    if (passwordToUse && safeUser?.phone) {
      saveRoomeetCreds(safeUser.phone, passwordToUse);
      console.log('[Login] 💾 creds ذخیره شد برای', safeUser.phone);
    }

    // ✅ ۲. context رو آپدیت کن
    authLogin(safeUser);

    // ✅ ۳. navigate کن
    // ✅ ۳. navigate بر اساس نقش کاربر
    const roleRoutes = {
      student: '/StudentHome',
      teacher: '/TeacherHome',
      parent: '/ParentHome',
      admin: '/AdminHome',
    };
    const targetRoute = roleRoutes[safeUser?.role] || '/login';
    navigate(targetRoute, { replace: true });

    // ✅ ۴. sync در پس‌زمینه
    if (!passwordToUse || !safeUser?.phone) {
      console.warn('[Login] ⚠️ بدون پسورد یا شماره — Roomeet sync انجام نمیشه');
      return;
    }

    setSyncStatus('syncing');
    console.log('[Login] 🔄 Roomeet sync شروع شد');

    withTimeout(
      syncUserWithRoomeet(
        { ...safeUser, password: passwordToUse },
        passwordToUse
      ),
      30000 // ✅ ۳۰ ثانیه — کل flow جا میشه
    )
      .then((roomeetData) => {
        if (!roomeetData) {
          console.warn('[Login] ⚠️ Roomeet sync نتیجه null داد');
          if (mountedRef.current) setSyncStatus('failed');
          return;
        }

        // ✅ حالت pendingLogin — کاربر اضافه شد ولی توکن نگرفت
        if (roomeetData.pendingLogin) {
          console.warn('[Login] ⚠️ کاربر اضافه شد ولی توکن نگرفت — AuthGate از خودش لاگین می‌کنه');
          if (mountedRef.current) setSyncStatus('done');
          return;
        }

        // ✅ حالت عادی — توکن گرفت
        if (roomeetData.roomeetToken) {
          const mergedUser = { ...safeUser, ...roomeetData };
          authUpdateUser(mergedUser);
          console.log('[Login] ✅ Roomeet sync موفق');
          if (mountedRef.current) setSyncStatus('done');
        } else {
          console.warn('[Login] ⚠️ پاسخ sync بدون توکن');
          if (mountedRef.current) setSyncStatus('failed');
        }
      })
      .catch((err) => {
        console.error('[Login] ❌ Roomeet sync error:', err.message);
        if (mountedRef.current) setSyncStatus('failed');
      });
  };

  // ============================================================
  //  توابع اصلی
  // ============================================================

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const phoneRegex = /^09[0-9]{9}$/;
    if (!phoneRegex.test(phone)) {
      setError('شماره موبایل معتبر نیست (مثال: 09123456789)');
      return;
    }

    setIsLoading(true);
    try {
      const foundUser = await authService.getUserByPhone(phone);
      if (foundUser) {
        setUser(foundUser);
        setCurrentStep(STEPS.LOGIN_PASSWORD);
      } else {
        await sendOtpForSignup(phone);
      }
    } catch (err) {
      console.error('❌ خطای کامل:', err);
      setError(err.message || 'خطا در ارتباط با سرور');
    } finally {
      setIsLoading(false);
    }
  };

  const sendOtpForSignup = async (phoneNumber) => {
    try {
      const otp = await authService.sendOtp(phoneNumber);
      setSentOtp(otp);
      setOtpTimer(60);
      setCanResend(false);
      setCurrentStep(STEPS.SIGNUP_VERIFY);
    } catch (err) {
      setError(err.message || 'خطا در ارسال کد تأیید');
    }
  };

  const handleOtpVerify = async () => {
    setError('');
    if (otpCode.length !== 6) {
      setError('کد باید ۶ رقم باشد');
      return;
    }
    if (!authService.verifyOtp(otpCode, sentOtp)) {
      setError('کد وارد شده نادرست است');
      return;
    }

    if (currentStep === STEPS.SIGNUP_VERIFY) {
      setCurrentStep(STEPS.SIGNUP_INFO);
      setOtpCode('');
      setSentOtp('');
    } else if (currentStep === STEPS.FORGOT_VERIFY) {
      setCurrentStep(STEPS.FORGOT_RESET);
      setOtpCode('');
      setSentOtp('');
    } else if (currentStep === STEPS.LOGIN_OTP) {
      await handleLoginSuccess(user);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (password.length < 8) {
      setError('رمز عبور حداقل ۸ کاراکتر');
      return;
    }

    setIsLoading(true);
    try {
      const loggedInUser = await authService.loginWithPassword(phone, password);
      if (loggedInUser) {
        await handleLoginSuccess(loggedInUser, password);
      } else {
        setError('رمز عبور اشتباه است');
      }
    } catch (err) {
      setError(err.message || 'خطا در ورود');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithOtp = async () => {
    setError('');
    setSuccessMessage('');
    try {
      const otp = await authService.sendOtp(phone);
      setSentOtp(otp);
      setOtpTimer(60);
      setCanResend(false);
      setCurrentStep(STEPS.LOGIN_OTP);
    } catch (err) {
      setError(err.message || 'خطا در ارسال کد یکبار مصرف');
    }
  };

  const handleForgotPassword = () => {
    setCurrentStep(STEPS.FORGOT_PASSWORD);
    setError('');
    setSuccessMessage('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const phoneRegex = /^09[0-9]{9}$/;
    if (!phoneRegex.test(phone)) {
      setError('شماره موبایل معتبر نیست');
      return;
    }

    setIsLoading(true);
    try {
      const foundUser = await authService.getUserByPhone(phone);
      if (!foundUser) {
        setError('کاربری با این شماره یافت نشد');
        setIsLoading(false);
        return;
      }
      setUser(foundUser);
      const otp = await authService.sendOtp(phone);
      setSentOtp(otp);
      setOtpTimer(60);
      setCanResend(false);
      setCurrentStep(STEPS.FORGOT_VERIFY);
    } catch (err) {
      setError(err.message || 'خطا در ارسال کد');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (newPassword.length < 8) {
      setError('رمز عبور حداقل ۸ کاراکتر');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('رمز عبور و تکرار آن مطابقت ندارند');
      return;
    }
    if (/[آ-ی]/.test(newPassword)) {
      setError('رمز عبور نباید شامل حروف فارسی باشد');
      return;
    }

    setIsLoading(true);
    try {
      await authService.updateUserPassword(phone, newPassword);
      setCurrentStep(STEPS.LOGIN_PASSWORD);
      setPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setSuccessMessage('✅ رمز عبور با موفقیت تغییر یافت. حالا با رمز جدید وارد شوید.');
    } catch (err) {
      setError(err.message || 'خطا در بروزرسانی رمز');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  //  توابع ثبت‌نام
  // ============================================================

  const handleCheckStudent = async () => {
    setError('');
    setSignupErrors((prev) => ({ ...prev, studentPhone: '' }));
    const studentPhone = signupData.studentPhone.trim();
    if (!/^09[0-9]{9}$/.test(studentPhone)) {
      setSignupErrors((prev) => ({
        ...prev,
        studentPhone: 'شماره موبایل دانش‌آموز معتبر نیست',
      }));
      return;
    }

    setIsLoading(true);
    try {
      const student = await authService.getUserByPhone(studentPhone);
      if (!student) {
        setSignupErrors((prev) => ({
          ...prev,
          studentPhone: 'این شماره وجود ندارد',
        }));
        setSignupData((prev) => ({ ...prev, studentInfo: null }));
      } else if (student.role !== 'student') {
        const roleMap = { teacher: 'معلم', parent: 'والدین', admin: 'مدیر سیستم' };
        setSignupErrors((prev) => ({
          ...prev,
          studentPhone: `این شماره مربوط به یک ${
            roleMap[student.role] || student.role
          } است، نه دانش‌آموز`,
        }));
        setSignupData((prev) => ({ ...prev, studentInfo: null }));
      } else {
        setSignupData((prev) => ({
          ...prev,
          studentInfo: student,
          studentField: '',
        }));
        setSignupErrors((prev) => ({ ...prev, studentPhone: '' }));
      }
    } catch (err) {
      setError('خطا در بررسی شماره دانش‌آموز');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupInfoSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSignupErrors({});

    const errors = {};
    if (!signupData.firstName.trim()) errors.firstName = 'نام را وارد کنید';
    if (!signupData.lastName.trim()) errors.lastName = 'نام خانوادگی را وارد کنید';
    if (!signupData.role) errors.role = 'نقش خود را انتخاب کنید';

    if (signupData.role === 'student' || signupData.role === 'teacher') {
      if (!signupData.grade) errors.grade = 'پایه تحصیلی را انتخاب کنید';
      if (needsField(signupData.grade) && !signupData.field) {
        errors.field = 'رشته تحصیلی را انتخاب کنید';
      }
    }

    if (signupData.role === 'parent') {
      if (!signupData.studentInfo) {
        errors.studentPhone = 'لطفاً شماره دانش‌آموز را بررسی و تأیید کنید';
      } else {
        // ✅ نرمال‌سازی — شاید داده‌ی قدیمی به شکل «پایه دهم» ذخیره شده باشد
        const studentGrade =
          normalizeGradeId(signupData.studentInfo.grade) ||
          signupData.studentInfo.grade ||
          '';
        if (needsField(studentGrade) && !signupData.studentField) {
          errors.studentField = 'لطفاً رشته تحصیلی دانش‌آموز را وارد کنید';
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setSignupErrors(errors);
      return;
    }

    setCurrentStep(STEPS.SIGNUP_PASSWORD);
  };

  const handleSignupPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const pwd = signupData.password || '';
    const confirm = signupData.passwordConfirm || '';

    if (pwd.length < 8) {
      setError('رمز عبور حداقل ۸ کاراکتر');
      return;
    }
    if (pwd !== confirm) {
      setError('رمز عبور و تکرار آن مطابقت ندارند');
      return;
    }
    if (/[آ-ی]/.test(pwd)) {
      setError('رمز عبور نباید شامل حروف فارسی باشد');
      return;
    }

    setIsLoading(true);
    try {
      let newUser = {
        
        firstName: signupData.firstName.trim(),
        lastName: signupData.lastName.trim(),
        phone: phone,
        role: signupData.role,
        password: pwd,
      };

      if (signupData.role === 'student' || signupData.role === 'teacher') {
        // ✅ ذخیره به‌صورت ID نرمال‌شده: '10' نه 'پایه دهم'
        newUser.grade = normalizeGradeId(signupData.grade) || signupData.grade;
        if (needsField(newUser.grade)) {
          newUser.field = normalizeFieldId(signupData.field) || signupData.field;
        }
      }

      if (signupData.role === 'parent' && signupData.studentInfo) {
        const student = signupData.studentInfo;
        newUser['firstName-student'] = student.firstName;
        newUser['lastName-student'] = student.lastName;
        // ✅ نرمال‌سازی پایه‌ی دانش‌آموز
        newUser['grade-student'] =
          normalizeGradeId(student.grade) || student.grade || '';
        // ✅ نرمال‌سازی رشته (اولویت: انتخاب والد، بعد داده‌ی دانش‌آموز)
        const normalizedStudentField =
          normalizeFieldId(signupData.studentField) ||
          normalizeFieldId(student.field) ||
          student.field ||
          '';
        if (normalizedStudentField) {
          newUser['field-student'] = normalizedStudentField;
        }
        newUser['phone-student'] = student.phone;
        newUser['id-student'] = student.id;
        newUser['coins-student'] = student.coins || 0;
        newUser['xp-student'] = student.xp || 0;
      }
    

      const createdUser = await authService.registerUser(newUser);
      console.log('[Login] ✅ کاربر ثبت‌نام شد:', createdUser.phone);

      // ✅ ثبت‌نام موفق → sync + navigate
      await handleLoginSuccess(createdUser, pwd);
    } catch (err) {
      setError(err.message || 'خطا در ثبت‌نام');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  //  رندر نشانگر قدرت رمز
  // ============================================================
  const renderPasswordStrength = (pwd) => {
    const strength = getPasswordStrength(pwd);
    const labels = ['ضعیف', 'متوسط', 'قوی'];
    const colors = ['#FF6B6B', '#FFD93D', '#34C759'];
    if (!pwd) return null;
    return (
      <div className="password-strength">
        <div className="strength-bar">
          <div
            className={`strength-fill strength-${strength}`}
            style={{
              width: `${(strength + 1) * 33.33}%`,
              background: colors[strength],
            }}
          />
        </div>
        <span className="strength-label" style={{ color: colors[strength] }}>
          {labels[strength]}
        </span>
      </div>
    );
  };

  // ============================================================
  //  نمایش وضعیت سینک Roomeet
  // ============================================================
  const renderSyncStatus = () => {
    if (syncStatus === 'syncing') {
      return (
        <div
          className="success-message"
          style={{
            background: 'rgba(59, 130, 246, 0.1)',
            borderColor: '#3b82f6',
            color: '#3b82f6',
            marginTop: '10px',
          }}
        >
          🔄 در حال اتصال به سرور کلاس‌ها...
        </div>
      );
    }
    return null;
  };

  // ============================================================
  //  رندر مراحل
  // ============================================================

  const renderPhoneStep = () => (
    <form className="auth-form" onSubmit={handlePhoneSubmit}>
      {/*<div className="step-icon">📱</div>*/}
      <h2>ورود به یادو</h2>
      <p className="step-desc">شماره موبایل خود را وارد کنید</p>

      <div className="form-group">
        <label>
          شماره موبایل <span className="required">*</span>
        </label>
        <div className={`phone-wrapper ${error ? 'error' : ''}`}>
          <span className="country-code">
            <span className="flag">🇮🇷</span> +98
          </span>
          <input
            type="tel"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            placeholder="0912"
            maxLength="11"
            dir="ltr"
            autoComplete="tel"
          />
        </div>
        {error && <span className="error-text">{error}</span>}
      </div>

      <button
        type="submit"
        className="btn btn-primary btn-large"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner" />
            در حال بررسی...
          </>
        ) : (
          <>
            ورود
            <i className="fas fa-arrow-left" />
          </>
        )}
      </button>
    </form>
  );

  const renderLoginPasswordStep = () => {
    const roleMap = {
      student: 'دانش‌آموز',
      teacher: 'معلم',
      parent: 'والدین',
      admin: 'مدیر سیستم',
    };
    return (
      <div className="auth-form">
        <div className="step-icon">🔐</div>
        <h2>خوش آمدید، {user?.firstName || 'کاربر'} 👋</h2>
        <p className="step-desc">
          نقش:{' '}
          <span className="role-badge">
            {roleMap[user?.role] || user?.role}
          </span>
        </p>

        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        <form onSubmit={handlePasswordLogin}>
          <div className="form-group">
            <label>
              رمز عبور <span className="required">*</span>
            </label>
            <div className={`input-wrapper ${error ? 'error' : ''}`}>
              <i className="fas fa-lock input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordStrength(getPasswordStrength(e.target.value));
                }}
                placeholder="رمز عبور خود را وارد کنید"
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i
                  className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                />
              </button>
            </div>
            {error && <span className="error-text">{error}</span>}
            {renderPasswordStrength(password)}
            {renderSyncStatus()}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner" />
                {syncStatus === 'syncing'
                  ? 'در حال اتصال به سرور کلاس‌ها...'
                  : 'در حال ورود...'}
              </>
            ) : (
              <>
                ورود به سامانه
                <i className="fas fa-arrow-left" />
              </>
            )}
          </button>
        </form>

        <div className="login-options">
          <button
            type="button"
            className="option-btn"
            onClick={handleLoginWithOtp}
          >
            <i className="fas fa-sms" />
            ورود با رمز یکبار مصرف
          </button>
          <button
            type="button"
            className="option-btn forgot-btn"
            onClick={handleForgotPassword}
          >
            <i className="fas fa-key" />
            فراموشی رمز عبور
          </button>
        </div>
      </div>
    );
  };

  const renderOtpStep = (title, desc) => (
    <div className="auth-form">
      <div className="step-icon">✉️</div>
      <h2>{title}</h2>
      <p className="step-desc">{desc}</p>

      <div className="form-group">
        <label>
          کد تأیید <span className="required">*</span>
        </label>
        <div className="otp-input-wrapper">
          <input
            ref={otpInputRef}
            type="text"
            inputMode="numeric"
            maxLength="6"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            className={`otp-input ${error ? 'error' : ''}`}
            placeholder="۶ رقم"
            dir="ltr"
          />
        </div>
        {error && <span className="error-text">{error}</span>}
        <div className="otp-timer">
          {otpTimer > 0 ? (
            <span>ارسال مجدد در {otpTimer} ثانیه</span>
          ) : (
            <button
              type="button"
              className="resend-btn"
              onClick={async () => {
                setError('');
                try {
                  const otp = await authService.sendOtp(phone);
                  setSentOtp(otp);
                  setOtpTimer(60);
                  setCanResend(false);
                } catch (err) {
                  setError(err.message);
                }
              }}
              disabled={!canResend}
            >
              ارسال مجدد کد
            </button>
          )}
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary btn-large"
        onClick={handleOtpVerify}
      >
        تأیید کد
        <i className="fas fa-check" />
      </button>
    </div>
  );

  const renderForgotStep = () => (
    <form className="auth-form" onSubmit={handleForgotSubmit}>
      <div className="step-icon">🔑</div>
      <h2>فراموشی رمز عبور</h2>
      <p className="step-desc">
        شماره موبایل خود را مجدداً وارد کنید تا کد تأیید برای شما ارسال شود
      </p>

      <div className="form-group">
        <label>
          شماره موبایل <span className="required">*</span>
        </label>
        <div className={`phone-wrapper ${error ? 'error' : ''}`}>
          <span className="country-code">
            <span className="flag">🇮🇷</span> +98
          </span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            placeholder="0912"
            maxLength="11"
            dir="ltr"
          />
        </div>
        {error && <span className="error-text">{error}</span>}
      </div>

      <button
        type="submit"
        className="btn btn-primary btn-large"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner" />
            در حال ارسال...
          </>
        ) : (
          <>
            ارسال کد تأیید
            <i className="fas fa-arrow-left" />
          </>
        )}
      </button>

      <button
        type="button"
        className="btn btn-secondary back-btn"
        onClick={() => {
          setCurrentStep(STEPS.LOGIN_PASSWORD);
          setError('');
        }}
      >
        <i className="fas fa-arrow-right" />
        بازگشت
      </button>
    </form>
  );

  const renderResetPasswordStep = () => (
    <form className="auth-form" onSubmit={handleResetPassword}>
      <div className="step-icon">🔄</div>
      <h2>تنظیم رمز جدید</h2>
      <p className="step-desc">رمز عبور جدید خود را وارد کنید</p>

      <div className="form-group">
        <label>
          رمز عبور جدید <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <i className="fas fa-lock input-icon" />
          <input
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setPasswordStrength(getPasswordStrength(e.target.value));
            }}
            placeholder="حداقل ۸ کاراکتر"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowNewPassword(!showNewPassword)}
          >
            <i
              className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}
            />
          </button>
        </div>
        {renderPasswordStrength(newPassword)}
      </div>

      <div className="form-group">
        <label>
          تکرار رمز عبور <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <i className="fas fa-check-circle input-icon" />
          <input
            type={showNewPasswordConfirm ? 'text' : 'password'}
            value={newPasswordConfirm}
            onChange={(e) => setNewPasswordConfirm(e.target.value)}
            placeholder="تکرار رمز عبور"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() =>
              setShowNewPasswordConfirm(!showNewPasswordConfirm)
            }
          >
            <i
              className={`fas ${
                showNewPasswordConfirm ? 'fa-eye-slash' : 'fa-eye'
              }`}
            />
          </button>
        </div>
      </div>

      {error && <span className="error-text">{error}</span>}

      <button
        type="submit"
        className="btn btn-primary btn-large"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner" />
            در حال ذخیره...
          </>
        ) : (
          <>
            ذخیره رمز جدید
            <i className="fas fa-save" />
          </>
        )}
      </button>
    </form>
  );

  const renderSignupInfoStep = () => (
    <form className="auth-form" onSubmit={handleSignupInfoSubmit}>
      <div className="step-icon">👤</div>
      <h2>تکمیل اطلاعات</h2>
      <p className="step-desc">لطفاً اطلاعات خود را وارد کنید</p>

      <div className="form-row">
        <div className="form-group half">
          <label>
            نام <span className="required">*</span>
          </label>
          <input
            type="text"
            value={signupData.firstName}
            onChange={(e) =>
              setSignupData((prev) => ({ ...prev, firstName: e.target.value }))
            }
            className={signupErrors.firstName ? 'error' : ''}
            placeholder="علی"
          />
          {signupErrors.firstName && (
            <span className="error-text">{signupErrors.firstName}</span>
          )}
        </div>

        <div className="form-group half">
          <label>
            نام خانوادگی <span className="required">*</span>
          </label>
          <input
            type="text"
            value={signupData.lastName}
            onChange={(e) =>
              setSignupData((prev) => ({ ...prev, lastName: e.target.value }))
            }
            className={signupErrors.lastName ? 'error' : ''}
            placeholder="محمدی"
          />
          {signupErrors.lastName && (
            <span className="error-text">{signupErrors.lastName}</span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>
          نقش شما <span className="required">*</span>
        </label>
        <select
          value={signupData.role}
          onChange={(e) => {
            const role = e.target.value;
            setSignupData((prev) => ({
              ...prev,
              role: role,
              grade: '',
              field: '',
              studentPhone: '',
              studentInfo: null,
              studentField: '',
            }));
          }}
          className={signupErrors.role ? 'error' : ''}
        >
          <option value="student">دانش‌آموز</option>
          <option value="teacher">معلم</option>
          <option value="parent">والدین</option>
        </select>
        {signupErrors.role && (
          <span className="error-text">{signupErrors.role}</span>
        )}
      </div>

      {(signupData.role === 'student' || signupData.role === 'teacher') && (
        <>
          <div className="form-group">
            <label>
              پایه تحصیلی <span className="required">*</span>
            </label>
            <select
              value={signupData.grade}
              onChange={(e) => {
                const grade = e.target.value;
                setSignupData((prev) => ({
                  ...prev,
                  grade,
                  // ✅ نگه‌داشتن رشته فقط اگر پایه نیاز به رشته دارد
                  field: needsField(grade) ? prev.field : '',
                }));
              }}
              className={signupErrors.grade ? 'error' : ''}
            >
              <option value="">انتخاب پایه...</option>
              {GRADE_LIST.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
            {signupErrors.grade && (
              <span className="error-text">{signupErrors.grade}</span>
            )}
          </div>

          {needsField(signupData.grade) && (
            <div className="form-group">
              <label>
                رشته تحصیلی <span className="required">*</span>
              </label>
              <select
                value={signupData.field}
                onChange={(e) =>
                  setSignupData((prev) => ({ ...prev, field: e.target.value }))
                }
                className={signupErrors.field ? 'error' : ''}
              >
                <option value="">انتخاب رشته...</option>
                {FIELD_LIST.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title}
                  </option>
                ))}
              </select>
              {signupErrors.field && (
                <span className="error-text">{signupErrors.field}</span>
              )}
            </div>
          )}
        </>
      )}

      {signupData.role === 'parent' && (
        <div className="form-group parent-section">
          <label>
            شماره موبایل دانش‌آموز <span className="required">*</span>
          </label>
          <div className="parent-phone-row">
            <div
              className={`phone-wrapper ${
                signupErrors.studentPhone ? 'error' : ''
              }`}
              style={{ flex: 1 }}
            >
              <span className="country-code">
                <span className="flag">🇮🇷</span> +98
              </span>
              <input
                type="tel"
                value={signupData.studentPhone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setSignupData((prev) => ({
                    ...prev,
                    studentPhone: val,
                    studentInfo: null,
                    studentField: '',
                  }));
                }}
                placeholder="0912"
                maxLength="11"
                dir="ltr"
              />
            </div>
            <button
              type="button"
              className="btn btn-secondary check-btn"
              onClick={handleCheckStudent}
              disabled={isLoading}
            >
              {isLoading ? <span className="spinner-small" /> : 'بررسی'}
            </button>
          </div>
          {signupErrors.studentPhone && (
            <span className="error-text">{signupErrors.studentPhone}</span>
          )}

          {signupData.studentInfo && (
            <div className="student-confirm">
              <div className="student-info">
                <i
                  className="fas fa-check-circle"
                  style={{ color: '#34C759' }}
                />
                <span>
                  <strong>
                    {signupData.studentInfo.firstName}{' '}
                    {signupData.studentInfo.lastName}
                  </strong>
                  {signupData.studentInfo.grade &&
                    ` (${getGrade(signupData.studentInfo.grade)?.title || signupData.studentInfo.grade})`}
                </span>
                <span className="confirm-label">
                  آیا این دانش‌آموز مورد نظر شماست؟
                </span>
              </div>
              <button
                type="button"
                className="btn btn-primary confirm-btn"
                onClick={() => {
                  // تأیید دانش‌آموز
                }}
              >
                <i className="fas fa-check" /> تأیید
              </button>

              {needsField(
                normalizeGradeId(signupData.studentInfo.grade) ||
                  signupData.studentInfo.grade ||
                  ''
              ) && (
                <div
                  className="form-group"
                  style={{ marginTop: '12px', width: '100%' }}
                >
                  <label>
                    رشته تحصیلی دانش‌آموز{' '}
                    <span className="required">*</span>
                  </label>
                  <select
                    value={signupData.studentField}
                    onChange={(e) =>
                      setSignupData((prev) => ({
                        ...prev,
                        studentField: e.target.value,
                      }))
                    }
                    className={signupErrors.studentField ? 'error' : ''}
                  >
                    <option value="">انتخاب رشته...</option>
                    {FIELD_LIST.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.title}
                      </option>
                    ))}
                  </select>
                  {signupErrors.studentField && (
                    <span className="error-text">
                      {signupErrors.studentField}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <span className="error-text" style={{ textAlign: 'center' }}>
          {error}
        </span>
      )}

      <button type="submit" className="btn btn-primary btn-large">
        ادامه
        <i className="fas fa-arrow-left" />
      </button>
    </form>
  );

  const renderSignupPasswordStep = () => (
    <form className="auth-form" onSubmit={handleSignupPasswordSubmit}>
      <div className="step-icon">🔑</div>
      <h2>تنظیم رمز عبور</h2>
      <p className="step-desc">رمز عبور خود را انتخاب کنید</p>

      <div className="form-group">
        <label>
          رمز عبور <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <i className="fas fa-lock input-icon" />
          <input
            type={showSignupPassword ? 'text' : 'password'}
            value={signupData.password || ''}
            onChange={(e) => {
              setSignupData((prev) => ({
                ...prev,
                password: e.target.value,
              }));
              setPasswordStrength(getPasswordStrength(e.target.value));
            }}
            placeholder="حداقل ۸ کاراکتر"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowSignupPassword(!showSignupPassword)}
          >
            <i
              className={`fas ${showSignupPassword ? 'fa-eye-slash' : 'fa-eye'}`}
            />
          </button>
        </div>
        {renderPasswordStrength(signupData.password || '')}
      </div>

      <div className="form-group">
        <label>
          تکرار رمز عبور <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <i className="fas fa-check-circle input-icon" />
          <input
            type={showSignupPasswordConfirm ? 'text' : 'password'}
            value={signupData.passwordConfirm || ''}
            onChange={(e) =>
              setSignupData((prev) => ({
                ...prev,
                passwordConfirm: e.target.value,
              }))
            }
            placeholder="تکرار رمز عبور"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() =>
              setShowSignupPasswordConfirm(!showSignupPasswordConfirm)
            }
          >
            <i
              className={`fas ${
                showSignupPasswordConfirm ? 'fa-eye-slash' : 'fa-eye'
              }`}
            />
          </button>
        </div>
      </div>

      {error && <span className="error-text">{error}</span>}
      {renderSyncStatus()}

      <button
        type="submit"
        className="btn btn-primary btn-large"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner" />
            {syncStatus === 'syncing'
              ? 'در حال اتصال به سرور کلاس‌ها...'
              : 'در حال ثبت‌نام...'}
          </>
        ) : (
          <>
            تکمیل ثبت‌نام
            <i className="fas fa-check" />
          </>
        )}
      </button>
    </form>
  );

  // ============================================================
  //  رندر اصلی
  // ============================================================
return (
  <div
    className={`login-page ${isDarkMode ? 'dark-mode' : ''}`}
    style={{ '--char-image': `url("${CHAR_IMAGES[currentStep]}")` }}
  >
      <div className="bg-art">
        <div className="orb orb--1" />
        <div className="orb orb--2" />
        <div className="orb orb--3" />
        <div className="orb orb--4" />
        <div className="bg-gradient" />
      </div>

      <div className="login-container glass">
        <div className="deco-ring" />
        <div className="deco-blur" />

        {BACK_TARGETS[currentStep] && (
          <button
            type="button"
            className="back-icon-btn"
            onClick={handleBack}
            title="بازگشت"
            aria-label="بازگشت"
          >
            <i className="fas fa-arrow-right" />
          </button>
        )}

        <div className="header">
        {/*<button
    type="button"
    className="theme-toggle"
    onClick={toggleTheme}
    title={isDarkMode ? 'حالت روشن' : 'حالت تیره'}
    aria-label={isDarkMode ? 'حالت روشن' : 'حالت تیره'}
  >
    {isDarkMode ? '☀️' : '🌙'}
  </button>*/}
          <div className="logo-wrapper">
            <img
              className="login-logo"
              src="/images/board-1.png"
              alt="لوگو"
            />
          </div>
        </div>

        <div className="step-content-wrapper">
          {currentStep === STEPS.PHONE && renderPhoneStep()}
          {currentStep === STEPS.LOGIN_PASSWORD && renderLoginPasswordStep()}
          {currentStep === STEPS.LOGIN_OTP &&
            renderOtpStep(
              'ورود با رمز یکبار مصرف',
              `کد ۶ رقمی برای شماره ${phone} ارسال شد`
            )}
          {currentStep === STEPS.FORGOT_PASSWORD && renderForgotStep()}
          {currentStep === STEPS.FORGOT_VERIFY &&
            renderOtpStep(
              'تأیید هویت',
              `کد تأیید برای شماره ${phone} ارسال شد`
            )}
          {currentStep === STEPS.FORGOT_RESET && renderResetPasswordStep()}
          {currentStep === STEPS.SIGNUP_VERIFY &&
            renderOtpStep(
              'تأیید شماره موبایل',
              `کد ۶ رقمی برای شماره ${phone} ارسال شد`
            )}
          {currentStep === STEPS.SIGNUP_INFO && renderSignupInfoStep()}
          {currentStep === STEPS.SIGNUP_PASSWORD && renderSignupPasswordStep()}
        </div>

        <div className="footer-decoration">
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </div>
      </div>
    </div>
  );
};

export default Login;